/**
 * Crew Chat hub (the cross-project chat index) — schemas for GET
 * /crew-chat/threads.
 *
 * One screen listing every PROJECT crew-chat the caller can access, as a flat,
 * sortable/searchable list (the page sorts/searches client-side). Since D1 chat
 * lives on the project, so does the hub: a project the caller can access appears
 * whether or not anyone has posted yet, so a crew member can start the first
 * message from here. It still only READS; posting/reading stays in
 * TaskChatPanel / the project-messages endpoints.
 *
 * Scope mirrors the per-project chat authz: crew see only projects they hold a
 * block on; supervisors/admins see every project. The PM/requester is never a
 * participant — the same Vari-crew-only boundary as the chat itself.
 *
 * Completed/cancelled projects are excluded by default; pass
 * ?include_completed=true to include them (the page's "Show completed" toggle).
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { ProjectStatusSchema } from './enums.js';
/** One project's chat, summarised for the list (a "chat log" row). */
export const CrewChatThreadSchema = z.object({
    project_id: UuidSchema,
    project_title: z.string(),
    client_name: z.string(),
    project_status: ProjectStatusSchema,
    /** Project's committed start (first approved day), null if unscheduled —
     *  drives the "Scheduled" sort + the row date. */
    scheduled_start: IsoDateTimeSchema.nullable(),
    /** Total messages on the project; 0 when no one has posted yet. */
    message_count: z.number().int().min(0),
    /** Newest message timestamp, or null when the project has no messages yet. */
    last_message_at: IsoDateTimeSchema.nullable(),
    /** ≤140-char preview of the newest message, or null when there are none. */
    last_message_excerpt: z.string().nullable(),
    /** Display name of the newest message's author, or null when there are none. */
    last_author_name: z.string().nullable(),
    /**
     * COALESCE(last_message_at, scheduled_start, created_at) — the default
     * "recent activity" sort key. Always set, so the client can sort without
     * null-handling.
     */
    activity_at: IsoDateTimeSchema,
});
/** GET /crew-chat/threads — a flat list; the page sorts/searches/groups it. */
export const CrewChatThreadsResponseSchema = z.object({
    threads: z.array(CrewChatThreadSchema),
});
//# sourceMappingURL=crew-chat.js.map